import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCompare } from '@/contexts/CompareContext';
import { X, GitCompareArrows, Car, Trash2 } from 'lucide-react';

export function CompareBar() {
  const { selectedCars, removeCar, clearAll, maxCars } = useCompare();

  if (selectedCars.length === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg animate-in slide-in-from-bottom-full duration-300 pb-[env(safe-area-inset-bottom)]">
      <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3">
        {/* Mobile: stacked layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          {/* Top row on mobile: count + actions */}
          <div className="flex items-center justify-between sm:hidden">
            <div className="flex items-center gap-2 min-w-0">
              <GitCompareArrows className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm font-medium truncate">
                {selectedCars.length}/{maxCars} valgt
                {selectedCars.length === 1 && (
                  <span className="text-muted-foreground font-normal"> · vælg 1 mere</span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearAll}
                className="h-11 w-11 p-0"
                aria-label="Ryd alle"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button 
                asChild 
                size="sm"
                disabled={selectedCars.length < 2}
                className={`h-11 px-4 text-sm font-medium ${selectedCars.length < 2 ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
              >
                <Link to="/sammenlign">
                  Sammenlign
                </Link>
              </Button>
            </div>
          </div>

          {/* Car chips - scrollable row */}
          <div className="flex items-center gap-2 flex-1 overflow-x-auto scrollbar-hide">
            <GitCompareArrows className="w-5 h-5 text-primary flex-shrink-0 hidden sm:block" />
            <span className="text-sm font-medium text-muted-foreground flex-shrink-0 hidden sm:block">
              Sammenlign ({selectedCars.length}/{maxCars}):
            </span>
            
            <div className="flex items-center gap-1.5 sm:gap-2">
              {selectedCars.map(car => (
                <div 
                  key={car.variant.id}
                  className="flex items-center gap-1.5 sm:gap-2 bg-muted rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex-shrink-0"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-background overflow-hidden flex-shrink-0">
                    {car.model.image_url ? (
                      <img 
                        src={car.model.image_url} 
                        alt={car.model.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs sm:text-sm font-medium max-w-[80px] sm:max-w-[120px] truncate">
                    {car.brand.name} {car.model.name}
                  </span>
                  <button
                    onClick={() => removeCar(car.variant.id)}
                    className="text-muted-foreground hover:text-foreground transition-colors min-w-[24px] min-h-[24px] flex items-center justify-center"
                    aria-label="Fjern fra sammenligning"
                  >
                    <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop actions */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={clearAll}
            >
              Ryd alle
            </Button>
            <Button 
              asChild 
              size="sm"
              disabled={selectedCars.length < 2}
              className={selectedCars.length < 2 ? 'opacity-50 cursor-not-allowed' : ''}
            >
              <Link to="/sammenlign">
                Sammenlign {selectedCars.length} elbiler
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
